import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { ShoppingCart, Timer, DollarSign, AlertTriangle } from "lucide-react";

const REC_META = {
  reorder_urgent: { label: "Reorder — Urgent", icon: AlertTriangle, color: "#D9381E" },
  reorder: { label: "Reorder", icon: ShoppingCart, color: "#14624A" },
  slow: { label: "Slow — capital tied up", icon: Timer, color: "#3B424B" },
  pricing: { label: "Pricing signal", icon: DollarSign, color: "#C9943D" },
};

function ConfBadge({ v }) {
  const map = { high: "bg-[#14624A]/10 text-[#14624A]", medium: "bg-[#C9943D]/15 text-[#8a6318]", low: "bg-[#3B424B]/10 text-[#3B424B]" };
  return <span className={`tm-badge ${map[v] || map.low}`}>{v}</span>;
}

export default function RecommendationsPage() {
  const [demo, setDemo] = useState(false);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [dismissed, setDismissed] = useState({});

  useEffect(() => {
    setLoading(true);
    api.get("/analytics/recommendations", { params: { demo } })
      .then(r => setRecs(r.data.recommendations || []))
      .finally(() => setLoading(false));
  }, [demo]);

  const filtered = filter === "all" ? recs : recs.filter((r) => r.recommendation === filter || (filter === "reorder" && r.recommendation === "reorder_urgent"));

  return (
    <DashboardLayout demoBanner={demo} demoMode={demo} onToggleDemo={() => setDemo(!demo)}>
      <h1 className="text-2xl sm:text-3xl font-bold" data-testid="recs-heading">Recommendations</h1>
      <p className="mt-1 text-sm text-[#3B424B]">Prioritized actions with the evidence behind each one. TreadMargin recommends. You decide.</p>

      <div className="mt-6 inline-flex bg-white rounded-lg p-1 border border-[var(--tm-border)]">
        {[["all", "All"], ["reorder", "Reorder"], ["slow", "Slow"], ["pricing", "Pricing"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${filter === k ? "bg-[var(--tm-charcoal)] text-white" : "text-[#3B424B]"}`} data-testid={`recs-filter-${k}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="mt-6 text-sm text-[#3B424B]">Loading…</div> : filtered.length === 0 ? (
        <div className="mt-6 tm-card p-8 bg-white" data-testid="recs-empty">No recommendations in this category right now.</div>
      ) : (
        <div className="mt-6 grid gap-3">
          {filtered.map((r) => {
            if (dismissed[r.sku]) return null;
            const meta = REC_META[r.recommendation] || REC_META.reorder;
            return (
              <div key={r.sku} className="tm-card p-5 bg-white flex gap-4" data-testid={`rec-card-${r.sku}`}>
                <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.color, color: meta.color === "#C9943D" ? "#0F1115" : "white" }}>
                  <meta.icon size={17} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">{meta.label}</span>
                    <ConfBadge v={r.confidence} />
                  </div>
                  <div className="mt-1 font-semibold">{r.sku} · {r.brand} {r.model} · <span className="font-mono">{r.tire_size}</span></div>
                  <p className="mt-2 text-sm text-[#3B424B]"><span className="font-semibold text-[#0F1115]">Why:</span> {r.recommendation_reason}</p>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div><div className="text-[#3B424B]">QOH</div><div className="font-mono font-semibold">{r.quantity_on_hand}</div></div>
                    <div><div className="text-[#3B424B]">Avg / day</div><div className="font-mono font-semibold">{r.avg_daily_sales?.toFixed?.(2) ?? "—"}</div></div>
                    <div><div className="text-[#3B424B]">Days supply</div><div className="font-mono font-semibold">{r.days_of_supply ?? "—"}</div></div>
                    <div><div className="text-[#3B424B]">Margin %</div><div className="font-mono font-semibold">{r.margin_pct != null ? `${r.margin_pct}%` : "—"}</div></div>
                  </div>
                  {r.recommendation.startsWith("reorder") && r.reorder_quantity > 0 && (
                    <div className="mt-3 text-sm"><span className="font-semibold">Recommend:</span> Reorder <span className="font-mono">{r.reorder_quantity}</span> units to reach target coverage.</div>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button className="tm-btn-outline h-9 px-3 text-xs" onClick={() => setDismissed({ ...dismissed, [r.sku]: true })} data-testid={`rec-dismiss-${r.sku}`}>Dismiss</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
